import { Box, VStack, Stack } from '@chakra-ui/react';
import { t } from '@lingui/core/macro';
import { CustomStepper } from '@duke-hq/ui/components/StepWizard/CustomStepper';
import { useStep } from '@duke-hq/ui/components/StepWizard/useStep';
import { SelectMembers } from '@duke-hq/app-provider-web/tenant/components/BulkEnrolments/SelectMembers';
import { SelectSkillSet } from '@duke-hq/app-provider-web/tenant/components/BulkEnrolments/SelectSkillSet';
import { ReviewCourse } from '@duke-hq/app-provider-web/tenant/components/BulkEnrolments/ReviewCourse';
import { SkillSetSettings } from '@duke-hq/app-provider-web/tenant/components/BulkEnrolments/SkillSetSettings';
import { SkillSetSubmission } from '@duke-hq/app-provider-web/tenant/components/BulkEnrolments/SkillSetSubmission';
import { SkillSetSummary } from '@duke-hq/app-provider-web/tenant/components/BulkEnrolments/SkillSetSummary';

interface StepComponentProps {
  goToNextStep: () => void;
  goToPrevStep: () => void;
  setStep: (step: React.SetStateAction<number>) => void;
}

interface BulkEnrolmentStepConfig {
  title: () => string;
  key: string;
  component: React.ComponentType<StepComponentProps>;
}

const BULK_ENROLMENT_STEPS: BulkEnrolmentStepConfig[] = [
  {
    title: () => t`Members`,
    key: 'SelectMembers',
    component: SelectMembers,
  },
  {
    title: () => t`Course`,
    key: 'SelectCourse',
    component: SelectSkillSet,
  },
  {
    title: () => t`Review`,
    key: 'ReviewCourse',
    component: ReviewCourse,
  },
  {
    title: () => t`Settings`,
    key: 'EnrolmentSettings',
    component: SkillSetSettings,
  },
  {
    title: () => t`Submit`,
    key: 'Submit',
    component: SkillSetSubmission,
  },
  {
    title: () => t`Summary`,
    key: 'Summary',
    component: SkillSetSummary,
  },
];

function BulkEnrolmentSteps({
  currentStep,
  goToNextStep,
  goToPrevStep,
  setStep,
}: {
  currentStep: number;
  goToNextStep: () => void;
  goToPrevStep: () => void;
  setStep: (step: React.SetStateAction<number>) => void;
}) {
  const stepConfig = BULK_ENROLMENT_STEPS[currentStep];

  if (!stepConfig) {
    return <Box>Error: Invalid Step</Box>;
  }

  const { key, component: Step } = stepConfig;

  const stepProps: StepComponentProps = {
    goToNextStep,
    goToPrevStep,
    setStep,
  };

  return <Step key={key} {...stepProps} />;
}

export function TeamBulkEnrolments() {
  const { currentStep, setStep, goToNextStep, goToPrevStep } = useStep({
    maxStep: BULK_ENROLMENT_STEPS.length - 1,
    initialStep: 0,
  });

  return (
    <VStack w="full" h="full" justify="start" gap="16px">
      <Box w="full">
        <Stack spacing="0" direction={{ base: 'column', md: 'row' }}>
          {BULK_ENROLMENT_STEPS.map((step, id) => (
            <CustomStepper
              key={step.key}
              cursor="pointer"
              onClick={() => {
                if (currentStep > id) {
                  setStep(id);
                }
              }}
              title={step.title()}
              isActive={currentStep === id}
              isCompleted={currentStep > id}
              isFirstStep={id === 0}
              isLastStep={BULK_ENROLMENT_STEPS.length === id + 1}
            />
          ))}
        </Stack>
      </Box>

      <Box w="full" h="full">
        <BulkEnrolmentSteps
          currentStep={currentStep}
          goToNextStep={goToNextStep}
          goToPrevStep={goToPrevStep}
          setStep={setStep}
        />
      </Box>
    </VStack>
  );
}
